import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Reveal, Stagger, StaggerItem, Eyebrow, Hairline } from "@/components/primitives";
import LivingImage from "@/components/LivingImage";
import Lightbox from "@/components/Lightbox";

// Dining, Arrival and Destination carry their film as the hover state.
// LivingImage owns the play grant globally; here we ask for hover-only
// activation so touch devices never fetch the loop.
const TYPES = [
  { id: "S26", type: "Rooms", film: "F19" },
  { id: "S25", type: "Dining", film: "F18" },
  { id: "S23", type: "Wellness", film: "F16" },
  { id: "S18", type: "Arrival", film: "F11" },
  { id: "S09", type: "Detail" },
  { id: "S07", type: "Landscape" },
  { id: "S24", type: "Beach club", film: "F17" },
  { id: "S27", type: "Destination", film: "F20" },
];

// Unnumbered on purpose — it belongs to Selected work, and the 01–11
// numbering of the locked section order stays intact.
export default function ShootTypes() {
  const [open, setOpen] = useState(null);

  return (
    <section className="px-6 pb-28 sm:px-10 md:pb-40" data-testid="shoot-types">
      <div className="mx-auto max-w-[1280px]">
        <Hairline className="mb-20" />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <Reveal>
            <Eyebrow>Browse by shoot type</Eyebrow>
            <h2
              className="display mt-6 text-primaryText"
              style={{
                fontSize: "clamp(2rem, 4vw, 3.25rem)",
                lineHeight: 1.05,
                fontWeight: 300,
              }}
            >
              The way a brief is written.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-sm leading-relaxed text-mutedText">
              Rooms to destination. Eight disciplines, each shown as a
              demonstration frame, <em>never</em> a client's. Three of them
              move when you rest on them.
            </p>
          </Reveal>
        </div>

        <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TYPES.map((t, i) => (
            <StaggerItem key={t.id}>
              <LivingImage
                still={t.id}
                film={t.film}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                onClick={() => setOpen(i)}
                testId={`shoot-type-${t.type.toLowerCase().replace(/\s+/g, "-")}`}
                alt={`${t.type}, open demonstration frame`}
                wrapperClassName="group relative block aspect-[4/5] w-full rounded-[2px] border border-hairline text-left glide hover:border-gold/50"
                className="atmos"
                parallax
                playOnHoverOnly
              >
                {/* stronger bottom scrim so the type name always reads;
                    a light top scrim protects the corner tick marks */}
                <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-ink/94 via-ink/50 to-transparent" aria-hidden="true" />
                <div className="absolute inset-x-0 top-0 h-[24%] bg-gradient-to-b from-ink/50 to-transparent" aria-hidden="true" />
                <div className="card-sweep" aria-hidden="true" />
                <div className="on-dark absolute inset-x-0 bottom-0 flex items-end justify-between p-5">
                  <span className="display text-2xl text-primaryText">{t.type}</span>
                  <span className="font-mono text-[9px] tracking-[0.2em] text-gold">
                    {t.film ? "HOVER · IT MOVES" : "DEMO FRAME"}
                  </span>
                </div>
                <span className="absolute right-4 top-4 h-3 w-3 border-r border-t border-brass/40" />
                {t.film && <span aria-hidden="true" data-cursor="play" className="absolute inset-0" />}
              </LivingImage>
            </StaggerItem>
          ))}
        </Stagger>

        <AnimatePresence>
          {open !== null && (
            <Lightbox
              items={TYPES}
              index={open}
              onClose={() => setOpen(null)}
              onIndex={setOpen}
            />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
